import type { RiteDefRow } from '../net/rites-api';
import { N_, t, formatNumber } from '../i18n/i18n';

/**
 * What a Rite does, said in one line, computed entirely from the server's own
 * numbers (2026-09-07).
 *
 * Same split `engine/config/talent-copy.ts` settled on and `shop.ts`'s
 * `consumableEffectLine` follows: **names and judgements are copy, every
 * quantity is read off the live def at render time.** A line here reading
 * "+25% XP" as a literal is a second place for a rebalance to be wrong, and
 * this project has already shipped a HUD that said "110 / 100" from exactly
 * that mistake — which is also why `rite-defs.ts`'s own prose carries no
 * percentages and a test enforces it.
 *
 * Every price in this shelf moved once already: the whole catalogue was
 * repriced when `rite-economy.test.ts` measured what a Vaults run really pays.
 * Nothing written here would have needed touching for that, and that is the
 * property being bought.
 */

/** The whole condition per floor, so each language can say "from the Cellars" its own way. */
const FLOOR_CONDITION: Record<string, string> = {
  CELLARS: N_('only if you extract from the Cellars'),
  OSSUARY: N_('only if you extract from the Ossuary'),
  VAULTS: N_('only if you extract from the Vaults'),
};

/** `1.25` -> `+25%`. Rounded, because a float here would read as a bug. */
function pct(multiplier: number): string {
  return `+${Math.round((multiplier - 1) * 100)}%`;
}

/** `5` -> `x5`, for multipliers big enough that a percentage stops reading as one. */
function times(multiplier: number): string {
  return `x${Number.isInteger(multiplier) ? multiplier : formatNumber(multiplier, 1)}`;
}

/**
 * The card's one line.
 *
 * Ordered so the *condition* lands last, because that is what the player is
 * actually deciding about — every Rite is a wager and the wager is the tail of
 * the sentence.
 */
export function riteEffectLine(def: RiteDefRow): string {
  const parts: string[] = [];

  if (def.killXpMultiplier !== undefined) {
    parts.push(t('{x} kill XP, no completion bonus', { x: times(def.killXpMultiplier) }));
  }
  if (def.completionXpMultiplier !== undefined) {
    parts.push(t('{x} completion bonus, no kill XP', { x: times(def.completionXpMultiplier) }));
  }
  if (def.xpMultiplier !== undefined) parts.push(t('{pct} XP', { pct: pct(def.xpMultiplier) }));
  if (def.partyXpMultiplier !== undefined) parts.push(t('{pct} XP to your squad', { pct: pct(def.partyXpMultiplier) }));
  if (def.carryBonusKg !== undefined) parts.push(t('+{n}kg carry', { n: formatNumber(def.carryBonusKg) }));
  if (def.rareChanceBonus !== undefined) parts.push(t('+{n}% rare chests', { n: Math.round(def.rareChanceBonus * 100) }));
  if (def.corpseLootCertain) parts.push(t('every corpse carries something'));
  if (def.corpseRareMultiplier !== undefined) parts.push(t('{x} rare spoils', { x: times(def.corpseRareMultiplier) }));
  if (def.silentLoad) parts.push(t('a full bag makes no noise'));

  // ' · ' is structural: item-detail.ts splits the condition back off on it.
  const effect = parts.length > 0 ? parts.join(' · ') : t('no effect');
  return `${effect} · ${riteConditionLine(def)}`;
}

/**
 * The condition, on its own, for the places that show it separately — the
 * detail sheet's own row and the Play panel's picker, where the question is
 * "will this pay on the run I am about to start?" rather than "what is it".
 */
export function riteConditionLine(def: RiteDefRow): string {
  if (def.requiresStratum) {
    const known = FLOOR_CONDITION[def.requiresStratum];
    // A floor this client has never heard of: its raw code, after the word
    // "floor" so a language with cases declines the noun, not the code.
    return known ? t(known) : t('only if you extract from floor {code}', { code: def.requiresStratum });
  }
  return def.paysOn === 'DIED' ? t('only if you die') : t('only if you extract');
}

/**
 * The cap, said plainly, or null when there is none.
 *
 * Shown rather than hidden because it is the difference between what the
 * multiplier promises and what the Rite pays, and a player who discovers it
 * only from a run-end number has been misled by their own shop. The Long
 * Lesson doubles a bad run almost exactly and a cleared Vaults run barely at
 * all, and that is worth being able to read before buying.
 */
export function riteCapLine(def: RiteDefRow): string | null {
  return def.maxBonusXp !== undefined ? t('at most +{n} XP', { n: def.maxBonusXp }) : null;
}

/** Why a burned Rite paid nothing, for the run-end screen. */
export function riteLapseLine(name: string, reason: 'wrong_outcome' | 'wrong_floor'): string {
  return reason === 'wrong_floor'
    ? t('{rite} — burned, but not on the floor it wanted', { rite: name })
    : t('{rite} — burned for nothing', { rite: name });
}
